import { useCallback, useEffect, useId, useMemo, useState } from 'react'

/**
 * useModalOpen
 * author: soojin.oh
 */

const idMap = {}

type Hook = (param: { id: string }) => [boolean, (isOpened: boolean) => void]
// hook
const useModalHash: Hook = function ({ id }) {
  const uniqueId = useId().replace(/:/g, '')
  const modalId = useMemo(() => `${id}-${uniqueId}`, [id, uniqueId])

  const getHashInfo = (modalId: string) => {
    const hash = window.location.hash
    const hashStack = hash.substring(1).split('|')
    const shouldOpen = hashStack.filter(dialogId => dialogId === modalId).length > 0
    return {
      hash,
      hashStack,
      shouldOpen,
    }
  }

  const [isOpened, setOpened] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false
    }
    return getHashInfo(modalId).shouldOpen
  })

  useEffect(() => {
    if (idMap[modalId]) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          `[warning] Modal ID 에 중복이 있습니다. 중복된 ID 는 "${modalId}" 입니다. 잘못 설정된 것이 아닌지 다시 확인해 주세요.`
        )
      }
    }
    idMap[modalId] = true
    return () => void (idMap[modalId] = false)
  }, [])

  useEffect(() => {
    const onHashChange = () => {
      setOpened(getHashInfo(modalId).shouldOpen)
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [modalId])

  const setState = useCallback((isOpened: boolean) => {
    const { hashStack, shouldOpen: isExist } = getHashInfo(modalId)
    if (isOpened) {
      // 열때는 url 에 중복이 있는지 여부를 보고 해시에 아이디가 존재하지 않으면 추가한다
      if (!isExist) {
        const newHash = [...hashStack, modalId].filter(modalId => !!modalId).join('|')
        window.location.hash = newHash
      }
    } else {
      // 닫을 때는 url 에 아이디가 존재하는지 여부를 보고 history back을 한다.
      // url 에 아이디가 존재하지 않으면 history back 을 통해 온것이거나 이미 닫힌 모달이다.
      if (isExist) {
        history.back()
      }
    }
    // modalId 넣지 않도록 주의!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [isOpened, setState]
}

export default useModalHash
